import { useState, useEffect } from 'react'
import { sessionStorageKeys } from '@sketch/constants'

type PreviewPwdStorage = string[]

const getSessionValue = (): PreviewPwdStorage => {
  const storageValue = window.sessionStorage.getItem(
    sessionStorageKeys.previewPwd
  )
  return storageValue ? JSON.parse(storageValue) : []
}

const saveToSession = (passwords: PreviewPwdStorage): void => {
  const oldValue = window.sessionStorage.getItem(sessionStorageKeys.previewPwd)
  const newValue = JSON.stringify(passwords)

  window.sessionStorage.setItem(sessionStorageKeys.previewPwd, newValue)

  // Dispatch custom storage event to handle same-tab changes
  const storageEvent = new StorageEvent('storage', {
    key: sessionStorageKeys.previewPwd,
    storageArea: window.sessionStorage,
    newValue,
    oldValue: oldValue || '',
  })

  window.dispatchEvent(storageEvent)
}

/**
 * Sets a password for a specific preview ID
 * @param previewId - The preview identifier
 * @param password - The password to store
 */
export const setPreviewPassword = (
  previewId: string,
  password: string
): void => {
  const currentPasswords = getSessionValue()
  const entryFormat = `${previewId}:${password}`

  // Remove existing entry for this preview ID if it exists
  const filteredPasswords = currentPasswords.filter(
    entry => !entry.startsWith(`${previewId}:`)
  )

  // Add the new entry
  const updatedPasswords = [...filteredPasswords, entryFormat]

  saveToSession(updatedPasswords)
}

/**
 * Gets a password for a specific preview ID
 * @param previewId - The preview identifier
 * @returns The password for the preview ID, or undefined if not found
 */
export const getPreviewPassword = (previewId: string): string | undefined => {
  const currentPasswords = getSessionValue()

  const entry = currentPasswords.find(entry =>
    entry.startsWith(`${previewId}:`)
  )

  if (entry) {
    const [, password] = entry.split(':')
    return password
  }

  return undefined
}

/**
 * Immediately stores a password for a preview ID (synchronous)
 * Use this when you need to store a password and have it available immediately
 * @param previewId - The preview identifier
 * @param password - The password to store
 */
export const setPreviewPasswordSync = (
  previewId: string,
  password: string
): void => {
  const currentPasswords = getSessionValue()
  const entryFormat = `${previewId}:${password}`

  // Remove existing entry for this preview ID if it exists
  const filteredPasswords = currentPasswords.filter(
    entry => !entry.startsWith(`${previewId}:`)
  )

  // Add the new entry
  const updatedPasswords = [...filteredPasswords, entryFormat]

  saveToSession(updatedPasswords)
}

/**
 * Hook for managing preview passwords with reactive storage updates
 * @param previewID - The preview identifier
 * @param pwd - Optional password to store
 * @returns The current password for the preview ID
 */
export const usePreviewPwd = (
  previewID: string,
  pwd?: string
): string | undefined => {
  const [password, setPassword] = useState<string | undefined>(() =>
    getPreviewPassword(previewID)
  )

  // Store password only when pwd changes (avoid infinite loops)
  useEffect(() => {
    if (pwd) {
      setPreviewPassword(previewID, pwd)
    }
  }, [previewID, pwd])

  // Listen for storage changes (both cross-tab and same-tab)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === sessionStorageKeys.previewPwd) {
        setPassword(getPreviewPassword(previewID))
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [previewID])

  return password
}
